import React, { ReactNode, useCallback, useEffect, useState, Fragment } from 'react';
import './LoadableComponent.scss'
import 'placeholder-loading/dist/css/placeholder-loading.min.css'
import { LoadScript } from '../../types/LoadScript';
import { ScriptLoader } from './ScriptLoaderHelper';

/**
 * A component that makes a load operation in order to be displayed (such as a web component).
 * While it's being loaded, a placeholder element can be shown in its place until the load finishes.
 */

type Props = {
    scriptsToBeLoaded: LoadScript[],
    onErrorCallback?: () => void,
    onLoadCallback?: () => void,
    container?: React.ElementType,
    errorPlaceholder: React.ElementType,
    loadPlaceholder: React.ElementType,
    message: JSX.Element
    hasOutsideApiError?: boolean;
    children?: ReactNode;
}

export default function LoadableComponent(props: Props) {

    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [scriptsInfo, setScriptsInfo] = useState<LoadScript[]>([])
    const [scriptLoaders, setScriptLoaders] = useState<any>([])

    const _onError = useCallback(() => {
        setIsLoading(false)
        setHasError(true)
        if (props.onErrorCallback)
            props.onErrorCallback();
    }, [props])

    const _onLoad = useCallback(() => {
        setIsLoading(false)
        setHasError(false)

        if (props.onLoadCallback)
            props.onLoadCallback();
    }, [props])

    const handleOutsideApiError = useCallback(() => {
        if (props.hasOutsideApiError) {
            _onError()
        }
    }, [props, _onError])

    const _loadScripts = useCallback((useTimespan: boolean) => {
        if (scriptsInfo && scriptsInfo.length > 0) {
            const scriptLoadersAux: any = [];

            scriptsInfo.forEach((scriptInfo: LoadScript) => {
                if (scriptInfo.url) {
                    let scriptLoader = scriptLoaders[scriptInfo.url];
                    if (!scriptLoader) {
                        scriptLoaders[scriptInfo.url] = scriptLoader = new ScriptLoader(scriptInfo);
                    }
                    scriptLoadersAux.push(scriptLoader);
                }
            });

            setIsLoading(true)
            setHasError(false)
            scriptLoadersAux.forEach((scriptLoader: ScriptLoader) => {
                scriptLoader.load(_onLoad, _onError, useTimespan);
            });
        }

    }, [scriptsInfo, scriptLoaders, _onLoad, _onError])

    useEffect(() => {
        setScriptsInfo(props.scriptsToBeLoaded)
        _loadScripts(false)

        handleOutsideApiError()
    }, [props, handleOutsideApiError, _loadScripts])


    function _loadScriptsWithTimespan() {
        return _loadScripts(true);
    }

    /**
 * Adds an external container (if it exists) to the element, usally for styling it.
 * @param {*} element Element to be encapsulated by the container.
 * @returns Final element.
 */
    function _withContainer(element: JSX.Element) {
        const Container = props.container;
        if (Container)
            return <Container>{element}</Container>;
        else
            return element;
    }

    /**
 * Renders the error placeholder whenever an error occurs.
 * @returns Error placeholder.
 */
    function _renderError() {
        const ErrorPlaceholder = props.errorPlaceholder;
        const element =

            <div className='loadable-component-container'>
                <ErrorPlaceholder onReloadClick={_loadScriptsWithTimespan} message={props.message} />
            </div>

        return _withContainer(element);
    }

    function _renderLoading() {
        // If the component is still loading, include the load placeholder element.
        const LoadPlaceholder = props.loadPlaceholder;
        const element =

            <div className='loadable-component-container'>
                <LoadPlaceholder />
            </div>

        return _withContainer(element);
    }

    function getPlaceholder() {
        let placeholder;
        if (hasError)
            placeholder = _renderError();
        else if (isLoading)
            placeholder = _renderLoading();

        return placeholder
    }

    return (
        <Fragment>
            {getPlaceholder()}
            {props.children}
        </Fragment>
    )
}
